import { Injectable } from '@angular/core';
import { map, shareReplay, tap } from "rxjs/operators";
import { Observable, of } from 'rxjs';
import { RestService } from '../services/rest.service';
import { environment } from '../../environments/environment';

export interface RemoteUserInfo {
  _id: string;
  full_name: string;
  email?: string;
  contact_number?: string;
  profile_image_url?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  authUrl = `${environment.apiUrl}/`;
  currentUser: any;

  // The other identity space in play everywhere in chat (room participants, message
  // senders, ad ownership) is the backend's Mongo _id, not the phone/email a user
  // logs in with. Resolved once and cached — every chat screen needs it to tell
  // "my messages" apart from the other participant's.
  myUserId: string | null = localStorage.getItem('myUserId');

  private userInfoCache = new Map<string, Observable<RemoteUserInfo | null>>();

  // Resolved once per session from /user_profile and cached — support tickets
  // are the only place this matters (an "agent" sees everyone's tickets and can
  // close them; see app/view/support.py's extract_role branch on the backend).
  private roleRequest: Observable<string> | null = null;

  // Shared across every screen that shows a save/heart toggle (home listing cards,
  // the account page's Saved properties tab) so they all agree on what's saved
  // without each re-fetching the profile.
  favouritePostIds = new Set<string>();
  private favouritesRequest: Observable<Set<string>> | null = null;

  // A stored, explicitly-updated flag rather than re-deriving on every read.
  // Templates bind to this (not to loggedIn()) so a value doesn't change
  // mid-digest — an impure template method call here previously triggered
  // ExpressionChangedAfterItHasBeenCheckedError right as login completed,
  // since its return value flips true the instant the token is written.
  isLoggedIn: boolean;

  constructor(private restService: RestService) {
    this.isLoggedIn = this.computeLoggedIn();
  }

  loginPhone(contactNumber: string) {
    return this.restService.POST(this.authUrl + "login", { contact_number: contactNumber });
  }

  verifyPhone(contactNumber: string, verificationCode: string) {
    return this.restService.POST(this.authUrl + "verify_phone", { contactNumber, verificationCode }).pipe(
      map((response: any) => {
        const user = response;
        if (user.succeeded) {
          localStorage.setItem("token", user.jwt_token);
          localStorage.setItem("user", JSON.stringify(user));
          this.currentUser = user;
          this.isLoggedIn = true;
        }
        return user;
      })
    );
  }

  // Called once a new user (verifyPhone's requires_name) picks a name — same
  // endpoint the account page uses to edit it later.
  saveName(fullName: string) {
    return this.restService.POST(`${environment.apiUrl}/user_profile`, { full_name: fullName }, this.authHeaders()).pipe(
      tap(() => {
        let u: any = {};
        try { u = JSON.parse(localStorage.getItem('user') || '{}'); } catch {}
        u.full_name = fullName;
        localStorage.setItem('user', JSON.stringify(u));
        if (this.currentUser) this.currentUser.full_name = fullName;
      })
    );
  }

  loggedIn() {
    return this.computeLoggedIn();
  }

  authHeaders(): any {
    const token = localStorage.getItem('token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  }

  // Resolves the signed-in user's Mongo _id from their phone/email, caching it —
  // chat rooms, messages and ad ownership are all keyed by this id, not the
  // phone/email used to log in.
  resolveMyUserId(): Observable<string | null> {
    if (this.myUserId) return of(this.myUserId);
    let u: any = {};
    try { u = JSON.parse(localStorage.getItem('user') || '{}'); } catch {}
    const query = u.contact_number
      ? `contactNumber=${encodeURIComponent(u.contact_number)}`
      : u.email ? `email=${encodeURIComponent(u.email)}` : '';
    if (!query) return of(null);
    return this.restService.GET(`${environment.apiUrl}/user?${query}`, this.authHeaders()).pipe(
      map((resp: any) => resp?._id || null),
      tap(id => {
        if (id) { this.myUserId = id; localStorage.setItem('myUserId', id); }
      })
    );
  }

  // Looks up a user's name/phone by their Mongo _id — used to label the other
  // participant in a chat and to dial them. Shared + cached per id so a screen with
  // several messages/rows from the same user only triggers one request for them.
  getUserInfo(userId: string): Observable<RemoteUserInfo | null> {
    if (!userId) return of(null);
    if (!this.userInfoCache.has(userId)) {
      const req$ = this.restService
        .GET(`${environment.apiUrl}/user?userId=${encodeURIComponent(userId)}`, this.authHeaders())
        .pipe(
          map((resp: any) => (resp?._id ? resp as RemoteUserInfo : null)),
          shareReplay(1)
        );
      this.userInfoCache.set(userId, req$);
    }
    return this.userInfoCache.get(userId)!;
  }

  // Seeds favouritePostIds from the server. Cached for the session — call again
  // (e.g. after logout/login) only if the identity actually changed.
  loadFavourites(): Observable<Set<string>> {
    if (this.favouritesRequest) return this.favouritesRequest;
    this.favouritesRequest = this.restService.GET(`${environment.apiUrl}/user_profile`, this.authHeaders()).pipe(
      map((resp: any) => {
        const ids: string[] = resp?.profile?.favourite_posts || [];
        this.favouritePostIds = new Set(ids);
        return this.favouritePostIds;
      }),
      shareReplay(1)
    );
    return this.favouritesRequest;
  }

  isFavourite(postId: string): boolean {
    return this.favouritePostIds.has(postId);
  }

  // 'client' (default) or 'agent' — only 'agent' changes support-ticket behavior today.
  getRole(): Observable<string> {
    if (this.roleRequest) return this.roleRequest;
    this.roleRequest = this.restService.GET(`${environment.apiUrl}/user_profile`, this.authHeaders()).pipe(
      map((resp: any) => resp?.profile?.role || 'client'),
      shareReplay(1)
    );
    return this.roleRequest;
  }

  isAgent(): Observable<boolean> {
    return this.getRole().pipe(map(role => role === 'agent'));
  }

  toggleFavourite(postId: string): Observable<boolean> {
    return this.restService.POST(`${environment.apiUrl}/favourite_post`, { postId }, this.authHeaders()).pipe(
      map((resp: any) => {
        const favourited = !!resp?.favourited;
        if (favourited) this.favouritePostIds.add(postId); else this.favouritePostIds.delete(postId);
        return favourited;
      })
    );
  }

  clearSession(): void {
    this.currentUser = null;
    this.isLoggedIn = false;
    this.myUserId = null;
    this.favouritePostIds = new Set();
    this.favouritesRequest = null;
    this.roleRequest = null;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('myUserId');
  }

  private computeLoggedIn(): boolean {
    try {
      const token = localStorage.getItem("token");
      if (!token) return false;
      const expiry = (JSON.parse(atob(token.split('.')[1]))).expires_at_date_time;
      if (!expiry) return false;
      // Python datetime.__str__() uses a space; ISO 8601 requires T — normalise before parsing
      const expiryDate = new Date(String(expiry).replace(' ', 'T'));
      if (isNaN(expiryDate.getTime())) return false;
      return Math.floor(Date.now() / 1000) <= Math.floor(expiryDate.getTime() / 1000);
    } catch {
      return false;
    }
  }

}
